import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { get } from 'lodash-es';
import ReactLoading from 'react-loading';
import {
  Button,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment-timezone';

import { imagePath, myAuctionPath } from '../../../utilities/UrlPaths';
import updateStepTracker from '../diy-services/updateStepTracker';
import InlineAlert from '../../../utilities/form-components/InlineAlert';
import Pagination from '../diy-utils/Pagination';
import { catalog2HrWarning, catalogNoEdit } from '../../../utilities/StaticText';

const previewText = 'This is a preview of what your session will look like on Invaluable.';

const sizeOptions = [50, 100, 200];

class Step5 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      searchTerm: '',
      currentPage: 0,
      loading: true
    };
  }

  async componentDidMount() {
    const {
      match: {
        params: { catalogID, type }
      }
    } = this.props;

    const lotsPerPage = sizeOptions[0];
    const isBin = type.toLowerCase() === 'bin';

    /**
     * (bnikom) fetch catalog data
     * pureList: unmutated lot data
     * filterList: lot data with search params (if they exist)
     * paginatedList: lots that appear per page
     */
    try {
      const lotResponse = await axios.get(`/cps/app/catalog/${catalogID}/preview`);
      const catalog = get(lotResponse, 'data.data.catalog');

      const stateObj = {
        catalog,
        filterList: lotResponse.data.data.lots,
        loading: false,
        lotsPerPage,
        paginatedList: lotResponse.data.data.lots.slice(0, lotsPerPage),
        pureList: lotResponse.data.data.lots,
        status: lotResponse.data.httpStatus,
        twoHrsPassed: false
      };

      // Only need to worry about time zone / 2 hr logic for live/timed sales
      if (!isBin) {
        const twoHrResponse = await axios.get(`/cps/app/catalog/${catalogID}/saleTimeIn2Hours?`);
        const { eventDateUTCString, timeZone } = catalog;
        const catalogDate = moment
          .utc(eventDateUTCString)
          .tz(timeZone)
          .format('MMMM Do YYYY, h:mm A z');

        stateObj.catalogDate = catalogDate;
        stateObj.twoHrsPassed = twoHrResponse.data.data.in2Hours === 1;
      }

      this.setState(stateObj);
    } catch (error) {
      console.log('this is horrifying: ', error);
      this.setState({
        error,
        loading: false
      });
    }
  }

  submitStepFive = async option => {
    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    const isBin = type.toLowerCase() === 'bin';
    const stepNumber = isBin ? 5 : 6;

    await updateStepTracker(catalogID, stepNumber);

    if (option === 'continue') {
      this.props.history.push(
        `/session/${type}/${parseInt(groupID, 10)}/${catalogID}/${stepNumber}`
      );
    } else {
      this.props.history.push(myAuctionPath);
    }
  };

  /**
   * (bnikom)
   * filter through lots that:
   * 1. start with the same lot number or inventory number
   * 2. contain the search string in the lot title
   */
  handleSearch = () => {
    const { searchTerm, pureList, lotsPerPage } = this.state;

    const pageList = pureList.filter(
      lot => lot.lotNumber.startsWith(searchTerm)
        || lot.invNumber.startsWith(searchTerm)
        || lot.lotTitle.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const paginatedList = pageList.slice(0, lotsPerPage);
    this.setState({ paginatedList, currentPage: 0, filterList: pageList });
  };

  setSearchState = e => {
    // (bnikom) if the cancel button is pressed, reset the filterList + page
    if (e.target.value === '') {
      const { pureList, lotsPerPage } = this.state;
      this.setState({
        filterList: pureList,
        currentPage: 0,
        paginatedList: pureList.slice(0, lotsPerPage),
        searchTerm: e.target.value
      });
    } else {
      this.setState({ searchTerm: e.target.value });
    }
  };

  removeSearch = () => {
    const { pureList, lotsPerPage } = this.state;
    this.setState({
      filterList: pureList,
      currentPage: 0,
      paginatedList: pureList.slice(0, lotsPerPage),
      searchTerm: ''
    });
  };

  /**
   * (bnikom)
   * @memberof Step5
   * currentPageOffset is your current page - 1
   * starting slice index = multiply offset by number of lots per page
   * end slice index is your beginning index + the number of lots per page
   */
  handlePageClick = data => {
    const { lotsPerPage, filterList } = this.state;
    const currentPageOffset = data.selected;
    const begin = currentPageOffset * lotsPerPage;
    const end = begin + +lotsPerPage;

    const pageList = filterList.slice(begin, end);
    this.setState({ paginatedList: pageList, currentPage: currentPageOffset });
  };

  // (bnikom) set the number of lots to show per page,
  // and reset pagination with new numLots to show
  handleLotsPerPage = e => {
    const pageList = this.state.filterList.slice(0, e.target.value);
    this.setState({ lotsPerPage: e.target.value, paginatedList: pageList, currentPage: 0 });
  };

  render() {
    const { loading, error } = this.state;

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    } if (error) {
      return <InlineAlert />;
    }

    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;
    const {
      catalog: { city, country, currency, houseLogo, houseName, phone, state, title, timed },
      catalogDate,
      currentPage,
      twoHrsPassed,
      paginatedList,
      lotsPerPage,
      filterList,
      searchTerm
    } = this.state;

    const isBin = type.toLowerCase() === 'bin';

    // allow for missing state
    let catalogAddress = `${city}, `;
    if (state) catalogAddress += `${state}, `;
    catalogAddress += country;

    const numLots = filterList.length;

    const numPages = Math.ceil(numLots / lotsPerPage);
    const linkedFrom = isBin ? 'step4' : 'step5';

    const renderBinAuctionDescription = () => (
      <Fragment>
        <h1 className="mb-1">{houseName}</h1>
        <p className="mt-2">
          {catalogAddress}
          {phone ? ` | ${phone}` : ''}
        </p>
        <p className="mt-2">Contact Gallery</p>
      </Fragment>
    );

    const renderLotTitle = (lotNumber, lotTitle) => (
      <div>
        <h5 className="text-clamp-2">
          {lotNumber ? `${lotNumber}: ${lotTitle}` : lotTitle}
        </h5>
      </div>
    );

    const renderLots = (
      <Row className="m-0">
        {paginatedList.map(lot => {
          const { hiEst, loEst, lotNumber, lotTitle, price, primaryImg, lotID } = lot;

          /**
           * (bnikom) estimate logic:
           * no high estimate || low estimate, don't show estimate string
           * only low estimate exists, only show low estimate
           * only high estimate exists, set low estimate = 0
           * both exist, show both
           */
          let estimateLot = '';
          if (!hiEst) {
            estimateLot = `Est: ${currency}${loEst}`;
          } else if (!loEst) {
            estimateLot = `Est: ${currency}0 - ${currency}${hiEst}`;
          } else {
            estimateLot = `Est: ${currency}${loEst} - ${currency}${hiEst}`;
          }

          return (
            <Col xs={3} className="mb-4" key={`${lotID}`}>
              <div className="img-container d-flex justify-content-center align-items-center">
                {!twoHrsPassed && (
                  <Link
                    to={{
                      pathname: `/session/${type}/${groupID}/${catalogID}/lot/${lotID}`,
                      state: { linkedFrom }
                    }}
                    aria-label="Edit"
                    className="edit-button"
                  >
                    <FontAwesomeIcon icon="edit" className="color-blue" size="lg" />
                  </Link>
                )}
                <img
                  className="mw-100 mh-100"
                  src={primaryImg || `${imagePath}/images/NoImage.png`}
                  alt={primaryImg ? lotTitle : 'No Image'}
                />
              </div>
              <div className="mt-3 mb-4">
                {renderLotTitle(lotNumber, lotTitle)}
                {isBin && (
                  <div>
                    <span className="lot-estimate">
                      {currency}
                      {price}
                    </span>
                  </div>
                )}
                {(loEst || hiEst) && (
                  <div>
                    <span className="lot-estimate">{estimateLot}</span>
                  </div>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    );

    const renderSearchBar = (
      <FormGroup row>
        <Label for="catalogSearch" className="sr-only">
          Search
        </Label>
        <Col xs={9} className="pr-0">
          <InputGroup>
            <InputGroupText>
              <FontAwesomeIcon icon="search" />
            </InputGroupText>
            <Input
              type="search"
              name="search"
              id="catalogSearch"
              className="cancel-icon-search"
              placeholder={`Search by ${isBin ? 'item' : 'lot'} or inventory number, or title`}
              onChange={e => this.setSearchState(e)}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  this.handleSearch(e);
                }
              }}
              value={searchTerm}
            />
            <InputGroupText className="cancel-button">
              <button
                className="cancel-icon"
                type="button"
                aria-label="Cancel"
                onClick={() => this.removeSearch()}
              >
                <FontAwesomeIcon icon={ faCircleXmark } className="color-gray-bg" />
              </button>
            </InputGroupText>
          </InputGroup>
        </Col>
        <Col xs={3}>
          <Button className="w-100" color="primary" onClick={e => this.handleSearch(e)}>
            Search
          </Button>
        </Col>
      </FormGroup>
    );

    const renderStep4Link = (
      <Link
        aria-label="Bulk upload images"
        className="align-label-vertical btn-link float-right text-nowrap"
        to={`/session/${type}/${parseInt(groupID, 10)}/${catalogID}/${isBin ? 3 : 4}`}
      >
        <FontAwesomeIcon icon="images" />
        &nbsp;Bulk upload images
      </Link>
    );

    const renderLotsPerPage = (
      <FormGroup row>
        <Label xs={6} className="font-weight-bold text-right text-nowrap pr-0" for="lotsPerPage">
          {`${isBin ? 'Items' : 'Lots'} Per Page`}
        </Label>
        <Col xs={6}>
          <Input
            type="select"
            name="select"
            id="lotsPerPage"
            onChange={e => this.handleLotsPerPage(e)}
          >
            {sizeOptions.map(sizeOption => (
              <option key={sizeOption}>{sizeOption}</option>
            ))}
          </Input>
        </Col>
      </FormGroup>
    );

    return (
      <div className="pb-5 mw-25">
        {!twoHrsPassed && (
          <InlineAlert
            baseText={previewText}
            icon="check-circle"
            color="success"
            showPhones={false}
            uncontrolled={true}
          />
        )}
        {twoHrsPassed && (
          <InlineAlert
            baseText={catalogNoEdit}
            color="warning"
            icon="clock"
            titleText={catalog2HrWarning}
            showPhones={false}
            uncontrolled={true}
          />
        )}
        <Row>
          <Col xs={6}>{renderSearchBar}</Col>
          <Col xs={3}>{renderStep4Link}</Col>
          <Col xs={3}>{renderLotsPerPage}</Col>
        </Row>
        <section className="mb-5">
          <div className="catalog-container">
            <div className="auction-description m-3">
              {isBin ? (
                <Fragment>
                  {houseLogo ? (
                    <Row>
                      <Col className="logo-container mb-3" xs={3}>
                        <img className="mw-100 mh-100" src={houseLogo} alt="House Logo" />
                      </Col>
                      <Col xs={9}>{renderBinAuctionDescription()}</Col>
                    </Row>
                  ) : (
                    renderBinAuctionDescription()
                  )}
                </Fragment>
              ) : (
                <Fragment>
                  <h3 className="mb-1">{title}</h3>
                  <span>by {houseName}</span>
                  <div className="mt-2">
                    <span className="bold-invaluable">{catalogDate} </span>
                    <span className="auction-indicator">
                      <FontAwesomeIcon icon={timed ? 'clock' : 'rss'} />
                      &nbsp;{timed ? 'Timed' : 'Live'} Auction
                    </span>
                  </div>
                  <p className="mt-2">{catalogAddress}</p>
                </Fragment>
              )}
              <h4 className="font-weight-bold bordered-bottom bordered-top py-3">
                {`${numLots} ${isBin ? 'item' : 'lot'}${numLots > 1 ? 's' : ''}`}
              </h4>
            </div>
            <div>{renderLots}</div>
          </div>
          <Pagination
            handlePageClick={this.handlePageClick}
            numPages={numPages}
            currentPage={currentPage}
          />
        </section>
        <Row>
          <Col xs={2}>
            <Button
              role="link"
              className="w-100"
              color="primary"
              onClick={() => this.submitStepFive('continue')}
            >
              Continue
            </Button>
          </Col>
          <Col xs={2} className="d-flex align-items-center">
            <Button role="link" color="link" onClick={() => this.submitStepFive('saveAndExit')}>
              Save &amp; Exit
            </Button>
          </Col>
        </Row>
      </div>
    );
  }
}

export default withRouter(Step5);
